import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Box, TextField, InputAdornment, InputLabel, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Chip, Button, Tooltip, CircularProgress, Stack } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { useUpdateFileAltTextMutation, useUpdateFileMutation, useDeleteFileMutation } from "../../../../../../shell/services/mediaManager";
import { fileExtension } from "../../utils/fileUtils";
import { RenameFileModal } from "./RenameFileModal";
import { fileTypeToColor } from "../../utils/fileUtils";
import DriveFileRenameOutlineRoundedIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import WidgetsRoundedIcon from "@mui/icons-material/WidgetsRounded";
import DriveFolderUploadRoundedIcon from "@mui/icons-material/DriveFolderUploadRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { FileReplace } from "@zesty-io/material";
import DeleteFileModal from "./DeleteFileModal";
import { MoveFileDialog } from "./MoveFileDialog";
import { formatLocalized } from "shell/i18n/dates";
export const MEDIA_TITLE_MAXLENGTH = 255;
interface Props {
  id?: string;
  src?: string;
  filename?: string;
  groupId?: string;
  binId?: string;
  title?: string;
  user?: {
    email?: string;
    role?: string;
  };
  createdAt?: string;
  updatedAt?: string;
  handleCloseModal: () => void;
  setShowEdit: (show: boolean) => void;
  onOpenReplaceFileModal: () => void;
}
export const FileModalContent: FC<Props> = ({
  id,
  binId,
  src,
  filename,
  groupId,
  title,
  user,
  createdAt,
  updatedAt,
  handleCloseModal,
  setShowEdit,
  onOpenReplaceFileModal
}) => {
  const {
    t
  } = useTranslation();
  const [newTitle, setNewTitle] = useState(title);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isCopiedZuid, setIsCopiedZuid] = useState<boolean>(false);
  const [newFilename, setNewFilename] = useState<string>(filename.substring(0, filename.lastIndexOf(".")) || filename);
  const [showRenameFileModal, setShowRenameFileModal] = useState<boolean>(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(null);
  const [showMoveFileDialog, setShowMoveFileDialog] = useState(false);
  const openSettings = Boolean(showSettingsDropdown);
  const [deleteFile] = useDeleteFileMutation();
  const [updateFile, {
    reset: resetUpdate,
    isSuccess: isSuccessUpdate,
    isLoading: isLoadingUpdate
  }] = useUpdateFileMutation();
  const [updateFileAltTextMutation, {
    reset: resetUpdateAltText,
    isSuccess: isSuccessUpdateAltText,
    isLoading: isLoadingUpdateAltText
  }] = useUpdateFileAltTextMutation();
  const [showDeleteFileModal, setShowDeleteFileModal] = useState<boolean>(false);

  /**
   * @description Set initial values for the fields
   */
  // useEffect(() => {
  //   if (newTitle.current) {
  //     newTitle.current.value = title;
  //   }
  //   // if (fileExtension(filename) !== "No Extension") {
  //   //   setFileType(fileExtension(filename));
  //   // }
  // }, [title, filename]);

  /**
   * @description Used for copying the alttext's value
   */
  const handleCopyClick = (data: string, isZuid = false) => {
    navigator?.clipboard?.writeText(data).then(() => {
      if (isZuid) {
        setIsCopiedZuid(true);
      } else {
        setIsCopied(true);
      }
      setTimeout(() => {
        if (isZuid) {
          setIsCopiedZuid(false);
        } else {
          setIsCopied(false);
        }
      }, 1500);
    }).catch(err => {
      console.error(err);
    });
  };

  /**
   * @description Used to call api everytime the filename and alttext is updated
   */
  const handleUpdateMutation = (renamedFilename?: string, isAltTextUpdate?: boolean, newGroupId = groupId) => {
    // construct file type
    let constructedFileType = "";
    if (fileExtension(filename)) constructedFileType = `.${fileExtension(filename)}`;
    if (isAltTextUpdate) {
      updateFileAltTextMutation({
        id,
        body: {
          group_id: newGroupId,
          title: newTitle,
          filename: `${renamedFilename}${constructedFileType}` || `${newFilename}${constructedFileType}`
        }
      });
    } else {
      updateFile({
        id,
        previousGroupId: groupId,
        body: {
          group_id: newGroupId,
          title: newTitle,
          filename: `${renamedFilename}${constructedFileType}` || `${newFilename}${constructedFileType}`
        }
      });
    }
  };
  const onDeleteFile = async () => {
    await deleteFile({
      id,
      body: {
        group_id: groupId
      }
    }).unwrap();
    handleCloseModal();
  };
  useEffect(() => {
    setNewTitle(title);
    setNewFilename(filename.substring(0, filename.lastIndexOf(".")) || filename);
  }, [title, filename]);
  return <Box data-sentry-element="Box" data-sentry-component="FileModalContent" data-sentry-source-file="FileModalContent.tsx">
      {/* Delete File Modal */}
      {showDeleteFileModal && <DeleteFileModal onDeleteFile={onDeleteFile} filename={filename} onClose={() => setShowDeleteFileModal(false)} />}

      {/* Rename File Modal */}
      {showRenameFileModal && <RenameFileModal src={src} handleUpdateMutation={handleUpdateMutation} onSetNewFilename={setNewFilename} onClose={() => setShowRenameFileModal(false)} newFilename={newFilename} isSuccessUpdate={isSuccessUpdate} isLoadingUpdate={isLoadingUpdate} resetUpdate={resetUpdate} extension={fileExtension(filename)} />}

      {showMoveFileDialog && <MoveFileDialog handleGroupChange={(newGroupId: string) => {
      handleUpdateMutation(newFilename, false, newGroupId);
      setShowMoveFileDialog(false);
      handleCloseModal();
    }} binId={binId} onClose={() => {
      setShowMoveFileDialog(false);
    }} />}

      {/* Content Header */}
      <Stack direction="row" p={2.5} borderBottom={1} borderColor="border" justifyContent="space-between" alignItems="flex-start" data-sentry-element="Stack" data-sentry-source-file="FileModalContent.tsx">
        <Stack gap={1} data-sentry-element="Stack" data-sentry-source-file="FileModalContent.tsx">
          <Tooltip title={newFilename} placement="bottom-start" data-sentry-element="Tooltip" data-sentry-source-file="FileModalContent.tsx">
            <Box sx={{
            width: "235px"
          }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
              <Typography variant="body1" fontWeight={700} noWrap data-sentry-element="Typography" data-sentry-source-file="FileModalContent.tsx">
                {newFilename}
              </Typography>
            </Box>
          </Tooltip>
          <Chip label={fileExtension(filename) || "No Extension"} sx={{
          textTransform: "uppercase",
          backgroundColor: `${fileTypeToColor(fileExtension(filename))}.100`,
          color: `${fileTypeToColor(fileExtension(filename))}.600`,
          width: "fit-content"
        }} size="small" data-sentry-element="Chip" data-sentry-source-file="FileModalContent.tsx" />
        </Stack>
        <Box sx={{
        display: "flex",
        flexDirection: "row"
      }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
          <Tooltip placement="bottom-start" title={t("common.more")} data-sentry-element="Tooltip" data-sentry-source-file="FileModalContent.tsx">
            <IconButton onClick={evt => setShowSettingsDropdown(evt.currentTarget)} aria-controls={openSettings ? "settingsMenu" : undefined} aria-haspopup="true" aria-label={t("media.fileModalContentOpenSettingsAria")} aria-expanded={openSettings ? "true" : undefined} size="small" data-sentry-element="IconButton" data-sentry-source-file="FileModalContent.tsx">
              <MoreHorizRoundedIcon fontSize="small" data-sentry-element="MoreHorizRoundedIcon" data-sentry-source-file="FileModalContent.tsx" />
            </IconButton>
          </Tooltip>
          <Tooltip placement="bottom-start" title={t("media.fileModalContentRenameTooltip")} data-sentry-element="Tooltip" data-sentry-source-file="FileModalContent.tsx">
            <IconButton size="small" onClick={() => setShowRenameFileModal(true)} data-sentry-element="IconButton" data-sentry-source-file="FileModalContent.tsx">
              <EditIcon fontSize="small" data-sentry-element="EditIcon" data-sentry-source-file="FileModalContent.tsx" />
            </IconButton>
          </Tooltip>
          <Tooltip placement="bottom-start" title={t("media.fileModalContentReplaceTooltip")} data-sentry-element="Tooltip" data-sentry-source-file="FileModalContent.tsx">
            <IconButton size="small" aria-label={t("media.fileModalContentReplaceAria")} onClick={onOpenReplaceFileModal} data-sentry-element="IconButton" data-sentry-source-file="FileModalContent.tsx">
              <FileReplace fontSize="small" data-sentry-element="FileReplace" data-sentry-source-file="FileModalContent.tsx" />
            </IconButton>
          </Tooltip>
          <Tooltip placement="bottom-start" title={t("media.fileModalContentDeleteTooltip")} data-sentry-element="Tooltip" data-sentry-source-file="FileModalContent.tsx">
            <IconButton size="small" aria-label={t("media.fileModalContentDeleteAria")} onClick={() => setShowDeleteFileModal(true)} data-sentry-element="IconButton" data-sentry-source-file="FileModalContent.tsx">
              <DeleteIcon fontSize="small" data-sentry-element="DeleteIcon" data-sentry-source-file="FileModalContent.tsx" />
            </IconButton>
          </Tooltip>
          <Tooltip placement="bottom-start" title={t("media.fileModalContentCloseTooltip")} data-sentry-element="Tooltip" data-sentry-source-file="FileModalContent.tsx">
            <IconButton size="small" onClick={() => handleCloseModal()} aria-label={t("media.fileModalContentCloseAria")} data-sentry-element="IconButton" data-sentry-source-file="FileModalContent.tsx">
              <CloseIcon fontSize="small" data-sentry-element="CloseIcon" data-sentry-source-file="FileModalContent.tsx" />
            </IconButton>
          </Tooltip>

          {/* Settings Dropdown Menu */}
          <Menu id="settingsMenu" anchorEl={showSettingsDropdown} open={Boolean(showSettingsDropdown)} onClose={() => setShowSettingsDropdown(null)} anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }} transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }} data-sentry-element="Menu" data-sentry-source-file="FileModalContent.tsx">
            <MenuItem onClick={() => {
            setShowRenameFileModal(true);
            setShowSettingsDropdown(null);
          }} data-sentry-element="MenuItem" data-sentry-source-file="FileModalContent.tsx">
              <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FileModalContent.tsx">
                <DriveFileRenameOutlineRoundedIcon data-sentry-element="DriveFileRenameOutlineRoundedIcon" data-sentry-source-file="FileModalContent.tsx" />
              </ListItemIcon>
              <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="FileModalContent.tsx">
                {t("media.fileModalContentRenameMenuItem")}
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleCopyClick(id, true)} data-sentry-element="MenuItem" data-sentry-source-file="FileModalContent.tsx">
              <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FileModalContent.tsx">
                {isCopied ? <CheckIcon /> : <WidgetsRoundedIcon />}
              </ListItemIcon>
              <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="FileModalContent.tsx">{t("media.fileModalContentCopyZuid")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => setShowMoveFileDialog(true)} data-sentry-element="MenuItem" data-sentry-source-file="FileModalContent.tsx">
              <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FileModalContent.tsx">
                <DriveFolderUploadRoundedIcon data-sentry-element="DriveFolderUploadRoundedIcon" data-sentry-source-file="FileModalContent.tsx" />
              </ListItemIcon>
              <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="FileModalContent.tsx">{t("media.fileModalContentMoveTo")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => setShowDeleteFileModal(true)} data-sentry-element="MenuItem" data-sentry-source-file="FileModalContent.tsx">
              <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FileModalContent.tsx">
                <DeleteRoundedIcon data-sentry-element="DeleteRoundedIcon" data-sentry-source-file="FileModalContent.tsx" />
              </ListItemIcon>
              <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="FileModalContent.tsx">{t("common.delete")}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Stack>

      {/* Content Form */}
      <Box sx={{
      px: 2.5
    }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
        <Box sx={{
        mt: 2
      }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
          <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="FileModalContent.tsx">{t("media.fileModalContentTitleLabel")}</InputLabel>
          <InputLabel sx={{
          color: "text.secondary"
        }} data-sentry-element="InputLabel" data-sentry-source-file="FileModalContent.tsx">
            {t("media.fileModalContentTitleHelper")}
          </InputLabel>
          <TextField placeholder={t("media.fileModalContentTitlePlaceholder")} aria-label={t("media.fileModalContentTitleAria")} value={newTitle} onChange={event => {
          if (event.target.value.length > MEDIA_TITLE_MAXLENGTH) {
            return;
          }
          setNewTitle(event.target.value);
        }} multiline rows={3} fullWidth data-sentry-element="TextField" data-sentry-source-file="FileModalContent.tsx" />
          <Typography variant="body2" color="text.disabled" textAlign="right" mt={0.5} data-sentry-element="Typography" data-sentry-source-file="FileModalContent.tsx">
            {newTitle?.length ?? 0} / {MEDIA_TITLE_MAXLENGTH}
          </Typography>
          {newTitle !== title && <Button disabled={isLoadingUpdateAltText} size="small" sx={{
          mt: 1
        }} variant="contained" aria-label={t("media.fileModalContentSaveTitleAria")} onClick={() => handleUpdateMutation(newFilename, true)}>
              {isLoadingUpdateAltText ? <CircularProgress size="24px" color="inherit" /> : t("common.save")}
            </Button>}
        </Box>
        <Box sx={{
        mt: 3
      }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
          <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="FileModalContent.tsx">{t("media.fileModalContentFileUrlLabel")}</InputLabel>
          <TextField fullWidth value={src} InputProps={{
          readOnly: true,
          endAdornment: <InputAdornment position="end">
                  {isCopied ? <CheckIcon /> : <IconButton onClick={() => handleCopyClick(src)} data-cy="copy-file-url-btn">
                      <ContentCopyIcon />
                    </IconButton>}
                </InputAdornment>
        }} data-sentry-element="TextField" data-sentry-source-file="FileModalContent.tsx" />
        </Box>
        {["jpg", "jpeg", "png", "webp", "avif"].includes(fileExtension(filename)) && <Button sx={{
        mt: 1.5
      }} onClick={() => setShowEdit(true)} startIcon={<EditIcon color="action" />} color="inherit" variant="contained">
            {t("media.fileModalContentLaunchOtfEditor")}
          </Button>}
        {/* {user?.email && (
          <Box sx={{ mt: 3 }}>
            <Typography color="text.secondary" variant="body3">
              UPLOADED BY
            </Typography>
            <Box sx={{ display: "flex", mt: 1 }}>
              <Avatar
                sx={{ bgcolor: "grey.300", width: 40, height: 40 }}
                alt={user?.email || ""}
                src={`https://www.gravatar.com/avatar/${MD5(
                  user?.email || ""
                )}.jpg?s=40`}
              ></Avatar>
              <Box sx={{ pl: 2 }}>
                <Typography>{user?.email}</Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  {user?.role}
                </Typography>
              </Box>
            </Box>
          </Box>
         )} */}
        <Box sx={{
        mt: 3
      }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
          <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="FileModalContent.tsx">{t("media.fileModalContentZuidLabel")}</InputLabel>
          <TextField fullWidth value={id} data-cy="zuid-textfield" InputProps={{
          readOnly: true,
          endAdornment: <InputAdornment position="end">
                  {isCopiedZuid ? <CheckIcon /> : <IconButton onClick={() => handleCopyClick(id, true)} data-cy="copy-zuid-btn">
                      <ContentCopyIcon />
                    </IconButton>}
                </InputAdornment>
        }} data-sentry-element="TextField" data-sentry-source-file="FileModalContent.tsx" />
        </Box>
        <Box sx={{
        mt: 3
      }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
          <Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="FileModalContent.tsx">
            {t("media.fileModalContentUploadedOn")}
          </Typography>
          <Box sx={{
          display: "flex",
          mt: 1
        }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
            <Box sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
              <CalendarTodayIcon sx={{
              color: "action.active"
            }} data-sentry-element="CalendarTodayIcon" data-sentry-source-file="FileModalContent.tsx" />
            </Box>
            <Box sx={{
            pl: 2
          }} data-sentry-element="Box" data-sentry-source-file="FileModalContent.tsx">
              <Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="FileModalContent.tsx">
                {createdAt && formatLocalized(new Date(createdAt), "PPP")}
              </Typography>
              <Typography variant="body2" sx={{
              color: "text.secondary"
            }} data-sentry-element="Typography" data-sentry-source-file="FileModalContent.tsx">
                {createdAt && <>
                    {formatLocalized(new Date(createdAt), "EEE, h:mm a")}{" "}
                    {new Date(createdAt).toLocaleTimeString("en-US", {
                  timeZoneName: "short"
                }).split(" ").pop()}
                  </>}
              </Typography>
            </Box>
          </Box>
        </Box>
        {!!updatedAt && <Box sx={{
        mt: 3
      }}>
            <Typography variant="body2">
              {t("media.fileModalContentUpdatedOn")}
            </Typography>
            <Box sx={{
          display: "flex",
          mt: 1
        }}>
              <Box sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}>
                <CalendarTodayIcon sx={{
              color: "action.active"
            }} />
              </Box>
              <Box sx={{
            pl: 2
          }}>
                <Typography variant="body2">
                  {updatedAt && formatLocalized(new Date(updatedAt), "PPP")}
                </Typography>
                <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
                  {updatedAt && <>
                      {formatLocalized(new Date(updatedAt), "EEE, h:mm a")}{" "}
                      {new Date(updatedAt).toLocaleTimeString("en-US", {
                  timeZoneName: "short"
                }).split(" ").pop()}
                    </>}
                </Typography>
              </Box>
            </Box>
          </Box>}
      </Box>
    </Box>;
};